import React from 'react';
import PropTypes from 'prop-types';
import { Flex } from 'rebass/styled-components';
import styled from 'styled-components';
import theme from 'styled-theming';
import Select, { components } from 'react-select';
import makeAnimated from 'react-select/animated';
import { ReactComponent as IconChecked } from '../assets/icon-checkbox.svg';

const animatedComponents = makeAnimated();

const backgroundColor = theme('mode', {
  light: (props) => (props.automation.enabled ? props.theme.colors.whiteGrey : 'transparent'),
  dark: (props) => (props.automation.enabled ? props.theme.colors.charcoal : 'transparent'),
});

const placeholderColor = theme('mode', {
  light: (props) => props.theme.colors.deepGrey,
  dark: (props) => props.theme.colors.midGhost,
});

const activeBorderColor = theme('mode', {
  light: (props) => props.theme.colors.periwinkle,
  dark: (props) => props.theme.colors.brightCerulean,
});

const disabledBorderColor = theme('mode', {
  light: (props) => props.theme.colors.greyLight,
  dark: (props) => props.theme.colors.lavenderGray,
});

const valueBorderColor = theme('mode', {
  light: (props) => props.theme.colors.quartz,
  dark: (props) => props.theme.colors.whiteGrey,
});

const valueColor = theme('mode', {
  light: (props) => props.theme.colors.deepGrey,
  dark: (props) => props.theme.colors.glitter,
});

const valueBGColor = theme('mode', {
  light: (props) => props.theme.colors.ghostDark,
  dark: (props) => props.theme.colors.glitter,
});

const checkboxBorderColor = theme('mode', {
  light: (props) => props.theme.colors.greyLight,
  dark: (props) => props.theme.colors.isabelline,
});

const checkboxColor = theme('mode', {
  light: (props) => props.theme.colors.whiteGrey,
  dark: (props) => props.theme.colors.deepGrey,
});

const hoverColor = theme('mode', {
  light: (props) => props.theme.colors.lightBlue2,
  dark: (props) => props.theme.colors.cyanDarker,
});

const menuBackgroundColor = theme('mode', {
  light: (props) => props.theme.colors.whiteGrey,
  dark: (props) => props.theme.colors.greyDarker,
});

const hoverScrollBG = theme('mode', {
  light: (props) => props.theme.colors.auroMetalSaurus,
  dark: (props) => props.theme.colors.auroMetalSaurus,
});

const hoverScrollThumbBG = theme('mode', {
  light: (props) => props.theme.colors.greyLight,
  dark: (props) => props.theme.colors.paynesGrey,
});

const StyledSelect = styled(Select)`
  & .react-select__indicator.react-select__clear-indicator {
    display: none;
  }

  & .react-select__indicator.react-select__dropdown-indicator {
    display: none;
  }

  & .react-select__indicator-separator {
    display: none;
  }

  & .react-select__value-container.react-select__value-container--is-multi {
    background-color: ${backgroundColor};
    outline: none;
    border-radius: 4px;

    & .react-select__value-container--has-value {
      border: 1px solid ${activeBorderColor};
    }

    & .react-select__value-container--has-value:focus {
      border: none;
    }
  }

  & .react-select__control {
    max-height: 120px;
    overflow-y: auto;
    background-color: ${backgroundColor};
    border-radius: 3px;
    box-shadow: ${(props) => (props.theme.mode === 'dark' ? 'none' : 'inset -1px -1px 0px rgba(255, 255, 255, 0.06), inset 1px 1px 2px rgba(0, 0, 0, 0.11);')}
    transition: .3s;
    border: 1px solid ${disabledBorderColor};

    &::-webkit-scrollbar {
      background-color: transparent;
      width: 8px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: ${hoverScrollThumbBG};
      outline: none;
      border-radius: 8px;
  
      &:hover {
        background-color: ${hoverScrollBG};
      }
    }
  }

  & .react-select__control:hover {
    border: 1px solid transparent;
  }

  & .react-select__placeholder {
    color: ${placeholderColor};
  }

  & .react-select__control--is-focused,
  & .react-select__control--is-focused:hover,
  & .react-select__control--menu-is-open {
    border: 1px solid ${activeBorderColor};
  }

  & .react-select__multi-value {
    border: ${(props) => (props.theme.mode === 'dark' ? '2px' : '1px')} solid ${valueBorderColor};
    border-radius: 4px;
    background-color: ${(props) => (props.theme.mode === 'dark' ? 'transparent' : valueBGColor)};
    flex-direction: row-reverse;
    justify-content: flex-end;
    padding-right: 10px;

    & .react-select__multi-value__label {
      color: ${valueColor};
      font-size: 13px;
      line-height: 135%;
      padding-left: 0;
      padding-right: 0;
    }

    & .react-select__multi-value__remove {
      cursor: pointer;
      &:hover {
        background-color: ${hoverColor};
        color: ${valueBorderColor};
      }
    }

  }

  & .react-select__input {
    color: ${valueColor};
  }

  & .react-select__menu {
    background-color: ${menuBackgroundColor};
    cursor: pointer;
    margin-top: 1px;
    z-index: 2;

    &:after {
      content: '';
      position: absolute;
      width: 14px;
      height: 100%;
      background-color: rgba(255, 255, 255, 0.0001);
      right: -3px;
      top: 0;
    }

    & .react-select__menu-list {
      max-height: 310px;

      &.react-select__menu-list--is-multi {

        &::-webkit-scrollbar {
          background-color: transparent;
          width: 8px;
        }
        &::-webkit-scrollbar-thumb {
          background-color: ${hoverScrollThumbBG};
          outline: none;
          border-radius: 8px;
      
          &:hover {
            background-color: ${hoverScrollBG};
          }
        }

        & .react-select__option {
          display: flex;
          flex-direction: row;
          cursor: pointer;
          transition: .3s;
        }
  
        & .react-select__option:hover,
        & .react-select__option--is-focused {
          background-color: ${hoverColor};
          transition: .3s;
        }
  
        & .react-select__option--is-selected {
          background-color: transparent;
        }
  
        & input {
          display: none;
        }
  
        & label {
          cursor: pointer;
          color: ${valueColor};
        }
  
        & .react-select__checkbox {
          width: 16px;
          height: 16px;
          border: 2px solid ${checkboxBorderColor};
          box-sizing: border-box;
          border-radius: 2px;
          margin-right: 6px;
  
          &.react-select__checkbox--checked {
            background-color: ${activeBorderColor};
            border: none;

            & svg {
              stroke: ${checkboxColor};
            }
          }
        }
      }
    }
  }
`;

const Option = (props) => {
  const { isSelected, label } = props;
  return (
    <Flex
      flexDirection="row"
    >
      <components.Option {...props}>
        <input
          type="checkbox"
          defaultChecked={isSelected}
        />
        <Flex
          className={`react-select__checkbox ${isSelected ? 'react-select__checkbox--checked' : ''}`}
          alignItems="center"
          justifyContent="center"
          flexDirection="row"
        >
          {isSelected && <IconChecked />}
        </Flex>
        <label
          htmlFor="checkbox"
          className="react-select__checkbox-label"
        >
          {label}
        </label>
      </components.Option>
    </Flex>
  );
};

Option.propTypes = {
  isSelected: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
};

function SkillsSelector(props) {
  const {
    automation,
    isDisabled,
    options,
    onChange,
    value,
    isLoading,
  } = props;

  return (
    <StyledSelect
      label="Single select"
      options={options}
      isMulti="true"
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      backspaceRemovesValue={false}
      placeholder={isDisabled ? '' : 'Type to search skills'}
      automation={automation}
      classNamePrefix="react-select"
      isDisabled={isDisabled}
      components={{
        animatedComponents,
        Option,
      }}
      onChange={(data) => onChange(data || [])}
      value={value}
      menuShouldScrollIntoView
      menuPlacement="top"
      menuPosition="fixed"
    />
  );
}

SkillsSelector.defaultProps = {
  isLoading: [],
};

SkillsSelector.propTypes = {
  automation: PropTypes.object.isRequired,
  options: PropTypes.array.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.array.isRequired,
  isLoading: PropTypes.array,
};

export default SkillsSelector;
